import { useMemo, useState } from 'react';

import { EventTicket, EventTicketInfo, EventTicketsPrintButton } from 'core/Components';
import { usePlatformService } from 'core/Hooks';
import { DateTime } from 'luxon';
import { useParams, useSearchParams } from 'react-router-dom';
import { ConsumerRedeemableTicket, useListRedeemableTicketsByEvent, useShowEvent } from 'store/Api';
import { Badge, Box, Select, Stack, Title } from 'ui';

import { TicketsPageLayout } from '../../Tickets/TicketsPageLayout/TicketsPageLayout';
import { makeTicketInfo } from '../../Tickets/utils';
import classes from './WalletEventsPage.module.css';

const WalletEventsPage = () => {
  const Platform = usePlatformService();
  const params = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const [eventDates, setEventDates] = useState<Map<string, ConsumerRedeemableTicket['event_date']>>(
    new Map()
  );
  const [selectedEventDateId, setSelectedEventDateId] = useState<string>('all');

  const { data: event } = useShowEvent({
    eventId: params.eventId,
    causeId: searchParams.get('cause_id') ?? undefined,
  });
  const eventName = event?.name ?? '';

  const { data: tickets } = useListRedeemableTicketsByEvent({
    eventId: params.eventId,
  });

  const sortedTickets = useMemo(() => {
    const filteredTickets = (tickets ?? []).filter((ticket) => {
      if (selectedEventDateId === 'all') return true;
      return ticket.event_date.uuid === selectedEventDateId;
    });

    // Single pass to sort tickets into expired/non-expired
    const sortedTickets = filteredTickets.reduce<{
      expired: ConsumerRedeemableTicket[];
      nonExpired: ConsumerRedeemableTicket[];
      used: ConsumerRedeemableTicket[];
    }>(
      (acc, ticket) => {
        const ticketInfo = makeTicketInfo(ticket);
        if (!ticketInfo) return acc;

        // Add event date data to map if it doesn't exist
        if (!eventDates.has(ticket.event_date.uuid)) {
          setEventDates((prev) => {
            const newMap = new Map(prev);
            newMap.set(ticket.event_date.uuid, ticket.event_date);
            return newMap;
          });
        }

        if (ticketInfo.isExpired) {
          acc.expired.unshift(ticket); // Add to start to maintain reverse chronological
        } else if (ticketInfo.isUsed) {
          acc.used.unshift(ticket);
        } else {
          acc.nonExpired.unshift(ticket);
        }
        return acc;
      },
      { expired: [], nonExpired: [], used: [] }
    );

    return sortedTickets;
  }, [tickets, selectedEventDateId, eventDates]);

  const ticketsList = useMemo(() => {
    // Combine non-expired first, then expired
    return [...sortedTickets.nonExpired, ...sortedTickets.used, ...sortedTickets.expired].map(
      (ticket, index, { length }) => {
        const ticketInfo = makeTicketInfo(ticket);
        if (!ticketInfo) return null;

        return (
          <EventTicket
            key={ticketInfo.ticketId}
            className={classes.WalletEventsPage__ticket}
            eventThumbnail={ticketInfo.eventThumbnail}
            title={ticketInfo.eventTitle}
            subtitle={ticketInfo.eventDateTitle}
            eventStartDate={ticketInfo.eventStartDate}
            venueName={ticketInfo.venueName}
            addressLineOne={ticketInfo.addressLineOne}
            addressLineTwo={ticketInfo.addressLineTwo}
            city={ticketInfo.city}
            state={ticketInfo.state}
            zipCode={ticketInfo.zipCode}
            totalTickets={length}
            ticketId={ticketInfo.ticketId}
            ticketName={ticketInfo.ticketName}
            ticketNumber={index + 1}
            isExpired={ticketInfo.isExpired}
            isUsed={ticketInfo.isUsed}
            ticketQrCodeUrl={ticketInfo.ticketQrCodeUrl}
            ticketUrl={ticketInfo.ticketUrl}
          />
        );
      }
    );
  }, [sortedTickets.nonExpired, sortedTickets.used, sortedTickets.expired]);

  // Generate options for the event date select dropdown
  const options = useMemo(() => {
    const eventDateOptions = Array.from(eventDates.entries())
      .map(([uuid, eventDate]) => {
        const date = DateTime.fromISO(eventDate.starting_at).toFormat('ccc, LLL d');
        const time = DateTime.fromISO(eventDate.starting_at)
          .toLocaleString(DateTime.TIME_SIMPLE)
          .replace(' AM', 'am')
          .replace(' PM', 'pm');

        return {
          label: `${eventDate.title} · ${date} · ${time}`,
          value: uuid,
        };
      })
      .reverse();

    return [
      {
        label: 'All Dates',
        value: 'all',
      },
      ...eventDateOptions,
    ];
  }, [eventDates]);

  const pdfTicketList = useMemo(() => {
    return sortedTickets.nonExpired
      .map((ticket) => {
        const ticketInfo = makeTicketInfo(ticket);
        if (!ticketInfo) return null;

        return {
          ...ticketInfo,
          subtitle: ticketInfo.eventDateTitle,
        };
      })
      .filter(Boolean) as EventTicketInfo[];
  }, [sortedTickets.nonExpired]);

  return (
    <TicketsPageLayout
      title={
        <Stack gap="sm">
          <Box className={classes.WalletEventsPage__header}>
            <Title order={2}>{eventName}</Title>
            <Badge miw="fit-content">
              {tickets?.length} {tickets?.length === 1 ? 'Ticket' : 'Tickets'}
            </Badge>
          </Box>
          {eventDates.size > 1 && (
            <Select
              onChange={(eventDateId) => {
                if (!eventDateId) return;

                setSelectedEventDateId(eventDateId);
              }}
              defaultValue="all"
              leftSection="Date"
              data={options}
              comboboxProps={{
                withinPortal: false,
              }}
            />
          )}
        </Stack>
      }
      left={
        Platform.isDesktopBrowser ? (
          <Stack gap="xs" w="full">
            <EventTicketsPrintButton title={eventName} eventList={pdfTicketList} />
          </Stack>
        ) : null
      }
      right={ticketsList}
    />
  );
};

export { WalletEventsPage };
